import { useMemo, useState } from "react";
import { ApolloCache, useMutation, useQuery } from "@apollo/client";
import {
  CreateProfileInput,
  createProfileGQL,
  generateProfileTokenGQL,
  meAAAGQL,
  OtpAction,
  Profile as ProfileGQL,
  ProfileTokenType,
  profilesGQL,
  requestOtpGQL,
  UpdateProfileActionInput,
  updateProfileGQL,
  User,
  verifyOtpGQL,
} from "@onmo/graphql/graphql";
import { Profile, toProfile } from "../models/profile";

const SKIP_PROFILE_TOKEN = { context: { skipProfileToken: true } };

export const useProfiles = () => {
  const { data: meData } = useQuery<{ me: User }>(meAAAGQL);
  const { data, loading, refetch } = useQuery<{ profiles: ProfileGQL[] }>(
    profilesGQL,
    { ...SKIP_PROFILE_TOKEN, fetchPolicy: "cache-and-network" },
  );

  const [createProfileMutation] = useMutation(
    createProfileGQL,
    SKIP_PROFILE_TOKEN,
  );
  const [updateProfileMutation] = useMutation(updateProfileGQL);
  const [generateProfileTokenMutation] = useMutation<{
    generateProfileToken: { token: string };
  }>(generateProfileTokenGQL, SKIP_PROFILE_TOKEN);
  const [requestOtpMutation] = useMutation<{
    requestOtp: { requestId: string; expiresAt: string };
  }>(requestOtpGQL);
  const [verifyOtpMutation] = useMutation(verifyOtpGQL);

  const [isCreationLocked, setIsCreationLocked] = useState(false);

  const profiles = useMemo<Profile[]>(
    () => (data?.profiles ?? []).map(toProfile),
    [data],
  );
  const mainProfile = profiles.find((profile) => profile.isOwner);

  const createProfile = (input: CreateProfileInput) =>
    createProfileMutation({ variables: { input } });

  // updateProfile returns only a message, so the change is written to the cache
  const writeActionToCache = (
    cache: ApolloCache<unknown>,
    id: string,
    action: UpdateProfileActionInput,
  ) => {
    const { editDetails, lock, unlock, disableKidMode } = action;
    const isMain = id === mainProfile?.id;
    cache.modify({
      id: cache.identify({ __typename: "Profile", id }),
      fields: {
        ...(editDetails?.username && { username: () => editDetails.username }),
        ...(editDetails?.language && { language: () => editDetails.language }),
        ...(lock && { locked: () => true }),
        ...(lock && !isMain && { isPinProtected: () => true }),
        ...(unlock && { locked: () => false }),
        ...(unlock && !isMain && { isPinProtected: () => false }),
        ...(disableKidMode && { isKidProfile: () => false }),
      },
    });
  };

  const updateProfile = (
    id: string,
    action: UpdateProfileActionInput,
    verificationToken?: string,
  ) =>
    updateProfileMutation({
      variables: { input: { id, action } },
      context: verificationToken
        ? { profileToken: verificationToken }
        : undefined,
      update: (cache) => writeActionToCache(cache, id, action),
    });

  const generateToken = async (input: {
    id: string;
    pin?: string;
    accountPin?: string;
    type?: ProfileTokenType;
  }) => {
    const result = await generateProfileTokenMutation({
      variables: { input },
    });
    const token = result.data?.generateProfileToken?.token;
    if (!token) throw new Error("No profile token returned");
    return token;
  };

  const requestOtp = async () => {
    const result = await requestOtpMutation({
      variables: { input: { action: OtpAction.AccountPin } },
    });
    const requestId = result.data?.requestOtp?.requestId;
    if (!requestId) throw new Error("No OTP request id returned");
    return { requestId, expiresAt: result.data?.requestOtp?.expiresAt ?? "" };
  };

  const verifyOtp = (requestId: string, otp: string) =>
    verifyOtpMutation({ variables: { input: { requestId, otp } } });

  const saveAccountPin = (value: string, requestId: string) => {
    if (!mainProfile) return Promise.reject(new Error("No main profile"));
    return updateProfile(mainProfile.id, { accountPin: { value, requestId } });
  };

  return {
    profiles,
    mainProfileId: mainProfile?.id,
    loading,
    phone: meData?.me?.phone ?? "",
    hasAccountPin: mainProfile?.hasPin ?? false,
    isCreationLocked,
    saveCreationLock: (isLocked: boolean) => setIsCreationLocked(isLocked),
    refetch,
    createProfile,
    updateProfile,
    generateToken,
    requestOtp,
    verifyOtp,
    saveAccountPin,
  };
};

export type ProfileStore = ReturnType<typeof useProfiles>;
